import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";
import {
  HrEmployeeLeaveApprovalListDocument,
  HrEmployeeLeaveAssignedDaysBulkUpsertDocument,
  HrEmployeeLeaveAssignedDaysListDocument,
  HrEmployeeLeaveGetByIdDocument,
  HrEmployeeLeaveHistoryCreateDocument,
  HrEmployeeLeaveHistoryDeleteDocument,
  HrEmployeeLeaveHistoryListDocument,
  HrEmployeeLeaveHistoryUpdateDocument,
  HrEmployeeLeaveListPaginatedDocument,
  HrEmployeeLeaveTypeListDocument,
} from "api/generated";
import { ICommonListModel } from "api/services";
import { Optional } from "config";

import {
  IHrEmployeeLeaveAssignedDaysModel,
  IHrEmployeeLeaveAssignedDaysBulkUpsertVars,
  IHrEmployeeLeaveHistoryListModel,
  IHrEmployeeLeaveModel,
  IHrEmployeeLeavePaginatedVars,
  IHrEmployeeLeavePaginatedResponse,
  IHrEmployeeLeaveHistoryUpdateVars,
  IHrEmployeeLeaveHistoryCreateVars,
} from "./types";

export async function getHrEmployeeLeaveTypeList(): Promise<
  ICommonListModel[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeLeaveTypeListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.hrEmployeeLeaveTypeList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeLeaveApprovalList(): Promise<
  ICommonListModel[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeLeaveApprovalListDocument,
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeLeaveApprovalList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeLeaveHistoryList(
  employeeId: number
): Promise<IHrEmployeeLeaveHistoryListModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeLeaveHistoryListDocument,
      variables: {
        employeeId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeLeaveHistoryList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeLeaveListPaginated(
  input: IHrEmployeeLeavePaginatedVars
): Promise<Optional<IHrEmployeeLeavePaginatedResponse>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeLeaveListPaginatedDocument,
      variables: input as any,
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeLeaveListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeLeaveByEmployee(
  employeeId: number
): Promise<IHrEmployeeLeaveModel> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeLeaveGetByIdDocument,
      variables: {
        employeeId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeLeaveGetById ?? {};
  } catch (error) {
    throw error;
  }
}

export async function getHrEmployeeLeaveAssignedDaysList(
  employeeId: number
): Promise<IHrEmployeeLeaveAssignedDaysModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrEmployeeLeaveAssignedDaysListDocument,
      variables: {
        employeeId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.hrEmployeeLeaveAssignedDaysList ?? [];
  } catch (error) {
    throw error;
  }
}

export async function bulkUpsertHrEmployeeLeaveAssignedDays(
  input: IHrEmployeeLeaveAssignedDaysBulkUpsertVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeLeaveAssignedDaysBulkUpsertDocument,
      variables: input as any,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "HrEmployeeLeaveAssignedDays",
      fieldNames: [
        "hrEmployeeLeaveListPaginated",
        "hrEmployeeLeaveGetById",
        "hrEmployeeLeaveAssignedDaysList",
      ],
    });

    return res?.data?.hrEmployeeLeaveAssignedDaysBulkUpsert ?? false;
  } catch (error) {
    throw error;
  }
}

export async function createHrEmployeeLeaveHistory(
  input: IHrEmployeeLeaveHistoryCreateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeLeaveHistoryCreateDocument,
      variables: input,
      fetchPolicy: "no-cache",
    });

    // Evict leave history data after creation
    executeEviction(client, {
      typename: "HrEmployeeLeave",
      fieldNames: ["hrEmployeeLeaveHistoryList", "hrEmployeeLeaveGetById"],
    });

    return res?.data?.hrEmployeeLeaveHistoryCreate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function updateHrEmployeeLeaveHistory(
  input: IHrEmployeeLeaveHistoryUpdateVars
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeLeaveHistoryUpdateDocument,
      variables: input,
      fetchPolicy: "no-cache",
    });

    // Evict leave history data after update
    executeEviction(client, {
      typename: "HrEmployeeLeave",
      fieldNames: ["hrEmployeeLeaveHistoryList", "hrEmployeeLeaveGetById"],
    });

    return res?.data?.hrEmployeeLeaveHistoryUpdate ?? false;
  } catch (error) {
    throw error;
  }
}

export async function deleteHrEmployeeLeaveHistory(
  id: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrEmployeeLeaveHistoryDeleteDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    // Evict leave history data after deletion
    executeEviction(client, {
      typename: "HrEmployeeLeave",
      fieldNames: ["hrEmployeeLeaveHistoryList", "hrEmployeeLeaveGetById"],
    });

    return res?.data?.hrEmployeeLeaveHistoryDelete ?? false;
  } catch (error) {
    throw error;
  }
}
